// TyresPro Ltd — multilingual copy
// Languages: English, Polish, Urdu, Arabic, Chinese (Simplified), Romanian
// Keep phone numbers, brand names and location names untranslated.

export const LANGUAGES = [
  { code: "en", label: "English",    nativeLabel: "English",    flag: "🇬🇧", dir: "ltr" },
  { code: "pl", label: "Polish",     nativeLabel: "Polski",     flag: "🇵🇱", dir: "ltr" },
  { code: "ur", label: "Urdu",       nativeLabel: "اردو",        flag: "🇵🇰", dir: "rtl" },
  { code: "ar", label: "Arabic",     nativeLabel: "العربية",     flag: "🇸🇦", dir: "rtl" },
  { code: "zh", label: "Chinese",    nativeLabel: "简体中文",     flag: "🇨🇳", dir: "ltr" },
  { code: "ro", label: "Romanian",   nativeLabel: "Română",      flag: "🇷🇴", dir: "ltr" },
];

export const DEFAULT_LANGUAGE = "en";

const translations = {
  en: {
    nav: { services: "Services", areas: "Areas", faq: "FAQ", book: "Book", contact: "Contact", call: "Call", whatsapp: "WhatsApp" },
    hero: {
      badge: "24/7 Emergency Response · Liverpool & surrounding areas",
      line1: "Flat tyre?",
      line2: "24/7 service.",
      line3: "We come to you.",
      sub: "Liverpool & surrounding areas' trusted 24/7 mobile tyre fitting service. Roadside, driveway or workplace —",
      subBold: "average arrival 30–40 minutes",
      callBtn: "Call",
      whatsappBtn: "WhatsApp Now",
      bookBtn: "Book Now",
      trust: {
        avgArrivalLabel: "Avg arrival", avgArrivalValue: "30–40 mins",
        openNowLabel: "Open now", openNowValue: "24/7 · 365",
        coverageLabel: "Coverage", coverageValue: "All Merseyside",
        insuredLabel: "Fully insured", insuredValue: "Pro fitters",
      },
    },
    promise: {
      overline: "Why TyresPro",
      heading1: "Fast, local,",
      heading2: "and always answering.",
      cta: "Tap to call ·",
      items: [
        { headline: "30–40 min", sub: "average arrival" },
        { headline: "24 / 7", sub: "open day & night" },
        { headline: "Cars · Vans · 4x4", sub: "any vehicle" },
        { headline: "Liverpool & surrounding areas", sub: "we come to you" },
        { headline: "Fully insured", sub: "professional fitters" },
      ],
    },
    services: {
      overline: "Our Services",
      heading1: "Everything you need.",
      heading2: "Right where you are.",
      sub: "From emergency callouts to scheduled replacements — our fully kitted vans bring the workshop to your doorstep, driveway, or roadside.",
      badge247: "Emergency · 24/7",
      cta: "Call out a van now →",
      available: "Available 24/7",
      cards: {
        emergency: { title: "24/7 Emergency Mobile Tyre Fitting", desc: "Roadside breakdown? We dispatch immediately across Liverpool & surrounding areas — day or night." },
        puncture:  { title: "Puncture Repair", desc: "Fast, professional puncture repairs on-site — back on the road in no time." },
        newtyres:  { title: "New Tyres", desc: "A wide range of new tyres supplied and professionally fitted on-site for cars, vans and 4x4s." },
        parttyres: { title: "Part-Worn Tyres", desc: "Quality, safety-checked part-worn tyres — fully inspected and fitted on-site." },
        runflat:   { title: "Run-Flat Tyres", desc: "Specialist run-flat replacement service for BMW, Mini & performance vehicles." },
        vehicles:  { title: "Cars · Vans · 4x4", desc: "All vehicle types covered — from city runabouts to commercial vans and SUVs." },
      },
    },
    areas: {
      overline: "Areas We Cover",
      heading1: "Rapid response across",
      heading2: "Merseyside.",
      sub: "Wherever you are stuck — from city centre to suburban driveway — we'll be with you in minutes, not hours.",
      note: "Not sure if we cover your area? Give us a call — we travel further for urgent jobs across the wider North West.",
      chipsTitle: "What people call us for",
    },
    faq: {
      overline: "Frequently Asked",
      heading1: "Quick answers.",
      heading2: "Faster help.",
      sub: "Still got a question? The fastest way to get an answer is to just ring us.",
      callBtn: "Call",
      items: [
        { q: "What is your 48-hour cover policy?", a: "Any tyre replacement, puncture repair, or tyre-related work carried out by TyresPro Ltd is covered for 48 hours from the time of fitting. We are not responsible for any issues arising after this 48-hour period. Please inspect your tyre before driving off and let us know immediately if anything is not right." },
      { q: "Do you really operate 24 hours a day, 7 days a week?", a: "Yes. TyresPro Ltd is a genuine 24/7 mobile tyre service. We answer the phone day or night, weekends and bank holidays included — because flat tyres don't keep office hours." },
        { q: "How quickly can you reach me?", a: "Most jobs across Liverpool & surrounding areas are reached within 30–40 minutes. For roadside emergencies we prioritise dispatch and will give you a confirmed ETA when you call." },
        { q: "What types of vehicles do you fit tyres on?", a: "Cars, vans, light commercial vehicles, 4x4s and SUVs. We carry stock and fitting equipment for a wide range of brands, including run-flats and low-profile performance tyres." },
        { q: "Can you repair a puncture, or do I need a new tyre?", a: "Where the damage is in the repairable area and the tyre is roadworthy, we'll repair it on the spot. If the tyre is unsafe, we'll fit a replacement — new or quality part-worn — and quote up front so there are no surprises." },
        { q: "Do you charge a callout fee?", a: "Pricing is transparent and confirmed before we travel. There is a small mobile callout charge for roadside work, but it's all explained on the phone — no surprises on arrival." },
        { q: "How do I pay?", a: "We accept card, contactless, bank transfer and cash. A receipt is provided for every job — useful for fleet, business or insurance claims." },
      ],
    },
    booking: {
      overline: "Book a Visit",
      heading1: "Pick a day.",
      heading2: "We'll come to you.",
      intro: "For non-emergency jobs — choose a date and time window that suits you, and we'll confirm your slot by phone. For urgent help right now, call us directly anytime.",
      bullets: [
        "Same-day slots usually available",
        "Home, work or driveway — wherever suits",
        "We confirm by phone before travelling",
      ],
      formTitle: "Booking Request",
      fields: {
        name: "Your name *",
        phone: "Phone number *",
        location: "Your location *",
        vehicle: "Vehicle (make / model)",
        tyreSize: "Tyre size (if known)",
        service: "Service *",
        preferredDate: "Preferred date *",
        preferredTime: "Preferred time *",
        notes: "Notes",
      },
      placeholders: {
        name: "John Smith",
        phone: "07...",
        location: "Postcode or area",
        vehicle: "e.g. Ford Transit",
        tyreSize: "e.g. 205/55 R16",
        service: "Choose a service",
        date: "Choose a date",
        time: "Pick a time window",
        notes: "Anything else we should know?",
      },
      services: ["New Tyres", "Part-Worn Tyres", "Puncture Repair", "Run-Flat Replacement", "Other"],
      timeSlots: ["Morning (8am – 12pm)", "Afternoon (12pm – 5pm)", "Evening (5pm – 9pm)", "Anytime — call to confirm"],
      disclaimer: "By submitting you agree to be contacted by phone or WhatsApp to confirm.",
      submit: "Request Booking",
      sending: "Sending...",
      success: "Booking received — we'll call you back to confirm shortly.",
      errors: {
        missing: "Please add your name, phone and location.",
        service: "Please choose a service.",
        date: "Please pick a preferred date.",
        time: "Please pick a preferred time.",
        network: "Couldn't send. Please call us on",
      },
    },
    contact: {
      overline: "Get In Touch",
      heading1: "Need a tyre?",
      heading2: "Call us now.",
      intro: "The fastest way to get help is to ring or WhatsApp us directly — we usually answer in seconds. Or send an enquiry and we'll call you straight back.",
      phoneLabel: "Phone · 24/7",
      whatsappLabel: "WhatsApp",
      whatsappValue: "Chat with a fitter",
      emailLabel: "Email",
      serviceAreaLabel: "Service Area",
      serviceAreaValue: "Liverpool & surrounding areas",
      formTitle: "Enquiry Form",
      fields: {
        name: "Your name *",
        phone: "Phone number *",
        location: "Your location *",
        tyreSize: "Tyre size (if known)",
        message: "Message",
      },
      placeholders: {
        name: "John Smith",
        phone: "07...",
        location: "Postcode or area",
        tyreSize: "e.g. 205/55 R16",
        message: "Tell us what's happened — puncture, blowout, vehicle make/model...",
      },
      disclaimer: "We respond within minutes during the day, and call back fast at night.",
      submit: "Send Enquiry",
      sending: "Sending...",
      success: "Enquiry sent — we'll call you back shortly.",
      errors: {
        missing: "Please fill in your name, phone and location.",
        network: "Couldn't send — please call us directly on",
      },
    },
    footer: {
      overline: "Emergency Tyre Service",
      big1: "Got a flat?",
      big2: "Call",
      big3: "— we'll come to you.",
      callBtn: "Call Now",
      whatsappBtn: "WhatsApp",
      desc: "Liverpool & surrounding areas' go-to 24/7 mobile tyre fitting service. Vans on the road day and night.",
      exploreLabel: "Explore",
      contactLabel: "Contact",
      rights: "All rights reserved.",
      privacy: "Privacy",
      terms: "Terms",
      base: "24/7 · Liverpool & surrounding areas",
    },
    controls: {
      language: "Language",
      theme: "Theme",
      light: "Light",
      dark: "Dark",
    },
    policy: {
      label: "48-Hour Cover Policy",
      text: "Any tyre replacement, puncture repair, or tyre-related work is covered for 48 hours only. We are not responsible for any issues arising after this 48-hour period.",
      short: "48-hour cover only — see FAQ.",
    },
    reviews: {
      overline: "Google Reviews",
      heading1: "5.0 stars.",
      heading2: "From real customers.",
      verified: "Verified",
      googleReviews: "Google reviews",
      localGuide: "Local Guide",
      review: "review",
      reviews: "reviews",
      owner: "Owner",
      showReply: "Owner reply",
      hideReply: "Hide reply",
      footnote: "Every review is a real, verified customer from our Google Business Profile — auto-fetched by Google, never edited by us.",
      writeBtn: "Leave a Google review",
    },
    driverStatus: {
      online: "Driver online",
      updatedNow: "Updated just now",
      heading1: "A van is available",
      heading2: "near you right now.",
      sub: "Our nearest driver is on standby across Liverpool & surrounding areas — call now and we'll dispatch immediately.",
      etaLabel: "Est. arrival",
      etaValue: "30–40 mins",
      coverageLabel: "Coverage",
      coverageValue: "All Merseyside",
      callBtn: "Call",
      liveMap: "Live · Liverpool coverage",
      mapAreaLabel: "Liverpool & surrounding areas",
      mapCaption: "Vans on the road now",
    },
    howItWorks: {
      overline: "How It Works",
      heading1: "Simple.",
      heading2: "Three steps.",
      startNow: "Start now",
      footnote: "That's it — no waiting rooms, no queues, no towing. We turn up, we fit, you drive off.",
      steps: [
        { title: "Call or WhatsApp us", desc: "Ring us on 07301 799929 or WhatsApp. Tell us where you are and what's happened — we quote up front." },
        { title: "Driver on the way", desc: "Our nearest available fitter is heading to you within minutes. Live ETA confirmed on the phone before we set off." },
        { title: "Fitted ASAP", desc: "Roadside, driveway or car park — full workshop tools on board. We fit fast, you drive off." },
      ],
    },
  },
  pl: {
    nav: { services: "Usługi", areas: "Obszary", faq: "FAQ", book: "Rezerwacja", contact: "Kontakt", call: "Zadzwoń", whatsapp: "WhatsApp" },
    hero: {
      badge: "Pomoc 24/7 · Liverpool i okolice",
      line1: "Przebita opona?",
      line2: "Serwis 24/7.",
      line3: "Dojedziemy do Ciebie.",
      sub: "Zaufany mobilny serwis opon 24/7 w Liverpoolu i okolicach. Przy drodze, na podjeździe lub w pracy —",
      subBold: "średni czas dojazdu 30–40 minut",
      callBtn: "Zadzwoń",
      whatsappBtn: "Napisz na WhatsApp",
      bookBtn: "Zarezerwuj",
      trust: {
        avgArrivalLabel: "Śr. dojazd", avgArrivalValue: "30–40 min",
        openNowLabel: "Otwarte", openNowValue: "24/7 · 365",
        coverageLabel: "Zasięg", coverageValue: "Cały Merseyside",
        insuredLabel: "Ubezpieczeni", insuredValue: "Fachowcy",
      },
    },
    promise: {
      overline: "Dlaczego TyresPro",
      heading1: "Szybko, lokalnie,",
      heading2: "i zawsze odbieramy.",
      cta: "Kliknij, aby zadzwonić ·",
      items: [
        { headline: "30–40 min", sub: "średni dojazd" },
        { headline: "24 / 7", sub: "dzień i noc" },
        { headline: "Auta · Vany · 4x4", sub: "każdy pojazd" },
        { headline: "Liverpool i okolice", sub: "dojedziemy do Ciebie" },
        { headline: "Pełne ubezpieczenie", sub: "profesjonalni mechanicy" },
      ],
    },
    services: {
      overline: "Nasze Usługi",
      heading1: "Wszystko, czego potrzebujesz.",
      heading2: "Tam, gdzie jesteś.",
      sub: "Od awaryjnych wezwań po planowe wymiany — nasze w pełni wyposażone furgonetki przywożą warsztat pod Twoje drzwi, podjazd lub na drogę.",
      badge247: "Awaria · 24/7",
      cta: "Wezwij furgonetkę teraz →",
      available: "Dostępne 24/7",
      cards: {
        emergency: { title: "Mobilny serwis opon 24/7", desc: "Awaria przy drodze? Wysyłamy natychmiast w Liverpoolu i okolicach — dzień i noc." },
        puncture:  { title: "Naprawa przebicia", desc: "Szybka, profesjonalna naprawa przebicia na miejscu — natychmiastowy powrót na drogę." },
        newtyres:  { title: "Nowe opony", desc: "Szeroki wybór nowych opon dostarczanych i profesjonalnie zakładanych na miejscu do aut, vanów i 4x4." },
        parttyres: { title: "Opony używane", desc: "Wysokiej jakości opony używane po kontroli bezpieczeństwa — sprawdzone i założone na miejscu." },
        runflat:   { title: "Opony Run-Flat", desc: "Specjalistyczna wymiana Run-Flat dla BMW, Mini i pojazdów sportowych." },
        vehicles:  { title: "Auta · Vany · 4x4", desc: "Wszystkie typy pojazdów — od miejskich po vany i SUV-y." },
      },
    },
    areas: {
      overline: "Obsługiwane Obszary",
      heading1: "Szybka reakcja w",
      heading2: "Merseyside.",
      sub: "Gdziekolwiek utknąłeś — od centrum miasta po przedmieścia — będziemy u Ciebie w minuty, nie godziny.",
      note: "Nie wiesz, czy obsługujemy Twój obszar? Zadzwoń — dojeżdżamy dalej dla pilnych zleceń w całym North West.",
      chipsTitle: "Po co do nas dzwonią",
    },
    faq: {
      overline: "Najczęstsze Pytania",
      heading1: "Szybkie odpowiedzi.",
      heading2: "Szybsza pomoc.",
      sub: "Masz pytanie? Najszybciej odpowiemy telefonicznie.",
      callBtn: "Zadzwoń",
      items: [
        { q: "Jaka jest wasza 48-godzinna polityka gwarancyjna?", a: "Każda wymiana opony, naprawa przebicia lub inna praca związana z oponami wykonana przez TyresPro Ltd jest objęta gwarancją przez 48 godzin od momentu montażu. Nie ponosimy odpowiedzialności za problemy powstałe po tym 48-godzinnym okresie. Prosimy sprawdzić oponę przed odjazdem i natychmiast zgłosić nieprawidłowości." },
      { q: "Czy naprawdę pracujecie 24 godziny na dobę, 7 dni w tygodniu?", a: "Tak. TyresPro Ltd to prawdziwy mobilny serwis opon 24/7. Odbieramy telefon dzień i noc, także w weekendy i święta." },
        { q: "Jak szybko do mnie dojedziecie?", a: "Większość zleceń w Liverpoolu i okolicach realizujemy w 30–40 minut. Przy awariach potwierdzimy dokładny czas dojazdu podczas rozmowy." },
        { q: "Do jakich pojazdów zakładacie opony?", a: "Auta, vany, lekkie pojazdy dostawcze, 4x4 i SUV-y. Mamy sprzęt i opony wielu marek, w tym Run-Flat i niskoprofilowe." },
        { q: "Można naprawić przebicie, czy potrzebuję nowej opony?", a: "Jeśli uszkodzenie kwalifikuje się do naprawy, naprawiamy na miejscu. Jeśli opona nie nadaje się — założymy nową lub używaną i podamy cenę z góry." },
        { q: "Czy pobieracie opłatę za dojazd?", a: "Ceny są jasne i potwierdzane przed wyjazdem. Przy pomocy drogowej obowiązuje niewielka opłata za dojazd — wszystko wyjaśniamy telefonicznie." },
        { q: "Jak mogę zapłacić?", a: "Przyjmujemy karty, płatności zbliżeniowe, przelewy i gotówkę. Rachunek zawsze dołączamy — przydatny dla firm i ubezpieczeń." },
      ],
    },
    booking: {
      overline: "Zarezerwuj wizytę",
      heading1: "Wybierz dzień.",
      heading2: "Dojedziemy do Ciebie.",
      intro: "Dla zleceń niepilnych — wybierz datę i przedział czasowy, potwierdzimy telefonicznie. Pilna pomoc? Zadzwoń w każdej chwili.",
      bullets: [
        "Sloty tego samego dnia zwykle dostępne",
        "Dom, praca lub podjazd — gdzie Ci pasuje",
        "Potwierdzamy telefonicznie przed wyjazdem",
      ],
      formTitle: "Formularz Rezerwacji",
      fields: {
        name: "Imię i nazwisko *",
        phone: "Numer telefonu *",
        location: "Lokalizacja *",
        vehicle: "Pojazd (marka / model)",
        tyreSize: "Rozmiar opony (jeśli znany)",
        service: "Usługa *",
        preferredDate: "Data *",
        preferredTime: "Godzina *",
        notes: "Uwagi",
      },
      placeholders: {
        name: "Jan Kowalski",
        phone: "07...",
        location: "Kod pocztowy lub obszar",
        vehicle: "np. Ford Transit",
        tyreSize: "np. 205/55 R16",
        service: "Wybierz usługę",
        date: "Wybierz datę",
        time: "Wybierz godzinę",
        notes: "Coś jeszcze powinniśmy wiedzieć?",
      },
      services: ["Nowe Opony", "Opony Używane", "Naprawa Przebicia", "Wymiana Run-Flat", "Inne"],
      timeSlots: ["Rano (8:00 – 12:00)", "Popołudnie (12:00 – 17:00)", "Wieczór (17:00 – 21:00)", "Kiedykolwiek — potwierdzimy"],
      disclaimer: "Wysyłając zgadzasz się na kontakt telefoniczny lub WhatsApp.",
      submit: "Wyślij zapytanie",
      sending: "Wysyłanie...",
      success: "Rezerwacja przyjęta — oddzwonimy, aby potwierdzić.",
      errors: {
        missing: "Podaj imię, telefon i lokalizację.",
        service: "Wybierz usługę.",
        date: "Wybierz datę.",
        time: "Wybierz godzinę.",
        network: "Nie udało się wysłać. Zadzwoń pod",
      },
    },
    contact: {
      overline: "Skontaktuj się",
      heading1: "Potrzebujesz opony?",
      heading2: "Zadzwoń teraz.",
      intro: "Najszybciej pomożemy telefonicznie lub przez WhatsApp — zwykle odbieramy w sekundy. Możesz też wysłać zapytanie, a oddzwonimy.",
      phoneLabel: "Telefon · 24/7",
      whatsappLabel: "WhatsApp",
      whatsappValue: "Napisz do mechanika",
      emailLabel: "Email",
      serviceAreaLabel: "Obszar obsługi",
      serviceAreaValue: "Liverpool i okolice",
      formTitle: "Formularz Zapytania",
      fields: {
        name: "Imię i nazwisko *",
        phone: "Numer telefonu *",
        location: "Lokalizacja *",
        tyreSize: "Rozmiar opony (jeśli znany)",
        message: "Wiadomość",
      },
      placeholders: {
        name: "Jan Kowalski",
        phone: "07...",
        location: "Kod pocztowy lub obszar",
        tyreSize: "np. 205/55 R16",
        message: "Opisz sytuację — przebicie, awaria, marka/model pojazdu...",
      },
      disclaimer: "W dzień odpowiadamy w kilka minut, w nocy szybko oddzwaniamy.",
      submit: "Wyślij Zapytanie",
      sending: "Wysyłanie...",
      success: "Zapytanie wysłane — wkrótce oddzwonimy.",
      errors: {
        missing: "Podaj imię, telefon i lokalizację.",
        network: "Nie udało się wysłać — zadzwoń pod",
      },
    },
    footer: {
      overline: "Serwis Awaryjny",
      big1: "Masz przebicie?",
      big2: "Zadzwoń",
      big3: "— dojedziemy do Ciebie.",
      callBtn: "Zadzwoń Teraz",
      whatsappBtn: "WhatsApp",
      desc: "Numer 1 w Liverpoolu i okolicach — mobilny serwis opon 24/7. Furgonetki w trasie dzień i noc.",
      exploreLabel: "Nawigacja",
      contactLabel: "Kontakt",
      rights: "Wszelkie prawa zastrzeżone.",
      privacy: "Prywatność",
      terms: "Regulamin",
      base: "24/7 · Liverpool i okolice",
    },
    controls: { language: "Język", theme: "Motyw", light: "Jasny", dark: "Ciemny" },
    policy: {
      label: "Gwarancja 48 godzin",
      text: "Każda wymiana opony, naprawa przebicia lub praca związana z oponami jest objęta gwarancją tylko przez 48 godzin. Nie ponosimy odpowiedzialności za problemy powstałe po tym okresie 48 godzin.",
      short: "Gwarancja tylko 48 godzin — zobacz FAQ.",
    },
    reviews: {
      overline: "Opinie Google",
      heading1: "5.0 gwiazdek.",
      heading2: "Od prawdziwych klientów.",
      verified: "Zweryfikowane",
      googleReviews: "opinii Google",
      localGuide: "Lokalny przewodnik",
      review: "opinia",
      reviews: "opinii",
      owner: "Właściciel",
      showReply: "Odpowiedź właściciela",
      hideReply: "Ukryj odpowiedź",
      footnote: "Każda opinia to prawdziwy, zweryfikowany klient z naszego profilu Google — pobierane bezpośrednio z Google.",
      writeBtn: "Napisz opinię na Google",
    },
    driverStatus: {
      online: "Kierowca online",
      updatedNow: "Zaktualizowano teraz",
      heading1: "Furgonetka jest dostępna",
      heading2: "w pobliżu Ciebie.",
      sub: "Nasz najbliższy kierowca jest w gotowości w Liverpoolu i okolicach — zadzwoń, wyślemy natychmiast.",
      etaLabel: "Śr. dojazd",
      etaValue: "30–40 min",
      coverageLabel: "Zasięg",
      coverageValue: "Cały Merseyside",
      callBtn: "Zadzwoń",
      liveMap: "Na żywo · Liverpool",
      mapAreaLabel: "Liverpool i okolice",
      mapCaption: "Furgonetki w trasie",
    },
    howItWorks: {
      overline: "Jak To Działa",
      heading1: "Prosto.",
      heading2: "Trzy kroki.",
      startNow: "Zacznij teraz",
      footnote: "To wszystko — bez poczekalni, kolejek i lawet. Przyjeżdżamy, zakładamy, odjeżdżasz.",
      steps: [
        { title: "Zadzwoń lub napisz na WhatsApp", desc: "Zadzwoń pod 07301 799929 lub napisz. Powiedz gdzie jesteś i co się stało — wycena z góry." },
        { title: "Kierowca w drodze", desc: "Nasz najbliższy fachowiec jedzie do Ciebie w kilka minut. Potwierdzamy ETA telefonicznie." },
        { title: "Wymiana natychmiast", desc: "Droga, podjazd lub parking — pełen warsztat na pokładzie. Szybko wymieniamy, odjeżdżasz." },
      ],
    },
  },
  ur: {
    nav: { services: "خدمات", areas: "علاقے", faq: "سوالات", book: "بکنگ", contact: "رابطہ", call: "کال کریں", whatsapp: "واٹس ایپ" },
    hero: {
      badge: "24/7 ہنگامی امداد · لیورپول اور گرد و نواح",
      line1: "ٹائر پنکچر؟",
      line2: "24/7 سروس۔",
      line3: "ہم آپ کے پاس آئیں گے۔",
      sub: "لیورپول اور گرد و نواح کی قابل اعتماد 24/7 موبائل ٹائر فٹنگ سروس۔ سڑک پر، ڈرائیو وے یا کام کی جگہ —",
      subBold: "اوسط پہنچنے کا وقت 30–40 منٹ",
      callBtn: "کال",
      whatsappBtn: "واٹس ایپ کریں",
      bookBtn: "بکنگ کریں",
      trust: {
        avgArrivalLabel: "اوسط آمد", avgArrivalValue: "30–40 منٹ",
        openNowLabel: "ابھی کھلا", openNowValue: "24/7 · 365",
        coverageLabel: "کوریج", coverageValue: "پورا مرسی سائیڈ",
        insuredLabel: "مکمل بیمہ شدہ", insuredValue: "ماہر فٹرز",
      },
    },
    promise: {
      overline: "کیوں ٹائرز پرو",
      heading1: "تیز، مقامی،",
      heading2: "اور ہمیشہ جواب دینے والے۔",
      cta: "کال کے لیے ٹیپ کریں ·",
      items: [
        { headline: "30–40 منٹ", sub: "اوسط آمد" },
        { headline: "24 / 7", sub: "دن رات کھلا" },
        { headline: "کاریں · وین · 4x4", sub: "کوئی بھی گاڑی" },
        { headline: "لیورپول اور گرد و نواح", sub: "ہم آپ کے پاس آئیں گے" },
        { headline: "مکمل بیمہ شدہ", sub: "پیشہ ور فٹرز" },
      ],
    },
    services: {
      overline: "ہماری خدمات",
      heading1: "آپ کی ہر ضرورت،",
      heading2: "جہاں آپ ہیں۔",
      sub: "ہنگامی کالز سے شیڈولڈ تبدیلیوں تک — ہماری مکمل لیس وین ورکشاپ آپ کے دروازے، ڈرائیو وے یا سڑک پر لاتی ہے۔",
      badge247: "ایمرجنسی · 24/7",
      cta: "ابھی وین بلائیں ←",
      available: "24/7 دستیاب",
      cards: {
        emergency: { title: "24/7 ہنگامی موبائل ٹائر فٹنگ", desc: "سڑک پر پھنسے ہیں؟ ہم لیورپول اور گرد و نواح میں فوری روانہ ہوتے ہیں — دن یا رات۔" },
        puncture:  { title: "پنکچر کی مرمت", desc: "موقع پر تیز، پیشہ ورانہ پنکچر کی مرمت — فوری سڑک پر واپسی۔" },
        newtyres:  { title: "نئے ٹائر", desc: "کاروں، وینوں اور 4x4 کے لیے نئے ٹائروں کی وسیع رینج، پیشہ ورانہ طور پر موقع پر فٹ کی جاتی ہے۔" },
        parttyres: { title: "پارٹ ورن ٹائر", desc: "معیاری، حفاظتی جانچ شدہ پارٹ ورن ٹائر — مکمل معائنہ اور موقع پر فٹ۔" },
        runflat:   { title: "رن فلیٹ ٹائر", desc: "BMW، Mini اور پرفارمنس گاڑیوں کے لیے خصوصی رن فلیٹ تبدیلی سروس۔" },
        vehicles:  { title: "کاریں · وین · 4x4", desc: "تمام گاڑیاں کور — چھوٹی کاروں سے کمرشل وین اور SUV تک۔" },
      },
    },
    areas: {
      overline: "کور کیے گئے علاقے",
      heading1: "پورے علاقے میں فوری جواب",
      heading2: "مرسی سائیڈ۔",
      sub: "آپ جہاں بھی پھنسے ہوں — شہر کے مرکز سے مضافاتی ڈرائیو وے تک — ہم منٹوں میں پہنچیں گے، گھنٹوں میں نہیں۔",
      note: "یقین نہیں کہ ہم آپ کا علاقہ کور کرتے ہیں؟ کال کریں — ہم فوری کاموں کے لیے شمالی مغرب میں دور بھی جاتے ہیں۔",
      chipsTitle: "لوگ ہمیں کیوں کال کرتے ہیں",
    },
    faq: {
      overline: "اکثر پوچھے جانے والے",
      heading1: "فوری جوابات۔",
      heading2: "تیز مدد۔",
      sub: "سوال ہے؟ جواب حاصل کرنے کا تیز ترین طریقہ ہمیں کال کرنا ہے۔",
      callBtn: "کال",
      items: [
        { q: "آپ کی 48 گھنٹے کی پالیسی کیا ہے؟", a: "TyresPro Ltd کی جانب سے کی گئی کوئی بھی ٹائر کی تبدیلی، پنکچر کی مرمت، یا ٹائر سے متعلق کام فٹنگ کے وقت سے 48 گھنٹے تک کور ہے۔ اس 48 گھنٹے کی مدت کے بعد پیدا ہونے والے کسی بھی مسئلے کے ہم ذمہ دار نہیں ہیں۔ روانہ ہونے سے پہلے ٹائر کا معائنہ کریں اور کوئی مسئلہ ہو تو فوراً بتائیں۔" },
      { q: "کیا آپ واقعی 24 گھنٹے، ہفتے کے 7 دن کام کرتے ہیں؟", a: "ہاں۔ TyresPro Ltd ایک حقیقی 24/7 موبائل ٹائر سروس ہے۔ ہم دن رات، ویک اینڈ اور تعطیلات پر بھی فون اٹھاتے ہیں۔" },
        { q: "آپ مجھ تک کتنی جلدی پہنچ سکتے ہیں؟", a: "لیورپول اور گرد و نواح میں زیادہ تر کام 30–40 منٹ میں پہنچ جاتے ہیں۔ ہنگامی حالات میں کال پر آپ کو صحیح ETA دیں گے۔" },
        { q: "آپ کن گاڑیوں پر ٹائر لگاتے ہیں؟", a: "کاریں، وین، ہلکی کمرشل گاڑیاں، 4x4 اور SUV۔ ہمارے پاس مختلف برانڈز کے ٹائر اور آلات ہیں، بشمول رن فلیٹ۔" },
        { q: "کیا پنکچر ٹھیک ہو سکتا ہے یا نیا ٹائر چاہیے؟", a: "اگر نقصان قابل مرمت جگہ پر ہے تو موقع پر ٹھیک کر دیں گے۔ اگر ٹائر غیر محفوظ ہو تو نیا یا پارٹ ورن لگا دیں گے — پہلے سے قیمت بتا دیں گے۔" },
        { q: "کیا کال آؤٹ فیس لیتے ہیں؟", a: "قیمتیں شفاف اور سفر سے پہلے طے شدہ ہیں۔ سڑک کے کام کے لیے تھوڑی سی کال آؤٹ چارج ہے — فون پر سب واضح کر دیا جاتا ہے۔" },
        { q: "میں ادائیگی کیسے کروں؟", a: "ہم کارڈ، کنٹیکٹ لیس، بینک ٹرانسفر اور کیش قبول کرتے ہیں۔ ہر کام کے لیے رسید فراہم کی جاتی ہے۔" },
      ],
    },
    booking: {
      overline: "بکنگ کریں",
      heading1: "ایک دن چنیں۔",
      heading2: "ہم آپ کے پاس آئیں گے۔",
      intro: "غیر ہنگامی کاموں کے لیے — اپنی مرضی کی تاریخ اور وقت چنیں، ہم فون پر تصدیق کریں گے۔ ابھی مدد چاہیے تو براہ راست کال کریں۔",
      bullets: [
        "عام طور پر اسی دن کی سلاٹس دستیاب",
        "گھر، کام یا ڈرائیو وے — جہاں آپ کو موزوں ہو",
        "سفر سے پہلے فون پر تصدیق کرتے ہیں",
      ],
      formTitle: "بکنگ کی درخواست",
      fields: {
        name: "آپ کا نام *",
        phone: "فون نمبر *",
        location: "آپ کا مقام *",
        vehicle: "گاڑی (میک / ماڈل)",
        tyreSize: "ٹائر سائز (اگر معلوم ہو)",
        service: "سروس *",
        preferredDate: "پسندیدہ تاریخ *",
        preferredTime: "پسندیدہ وقت *",
        notes: "نوٹس",
      },
      placeholders: {
        name: "جان اسمتھ",
        phone: "07...",
        location: "پوسٹ کوڈ یا علاقہ",
        vehicle: "مثلاً Ford Transit",
        tyreSize: "مثلاً 205/55 R16",
        service: "ایک سروس چنیں",
        date: "تاریخ چنیں",
        time: "وقت چنیں",
        notes: "کچھ اور بتائیں؟",
      },
      services: ["نئے ٹائر", "پارٹ ورن ٹائر", "پنکچر کی مرمت", "رن فلیٹ تبدیلی", "دیگر"],
      timeSlots: ["صبح (8am – 12pm)", "دوپہر (12pm – 5pm)", "شام (5pm – 9pm)", "کسی بھی وقت — کال پر تصدیق"],
      disclaimer: "درخواست جمع کر کے آپ فون یا واٹس ایپ پر رابطے کی اجازت دیتے ہیں۔",
      submit: "بکنگ کی درخواست",
      sending: "بھیجا جا رہا ہے...",
      success: "بکنگ موصول ہو گئی — ہم جلد ہی کال کریں گے۔",
      errors: {
        missing: "نام، فون اور مقام درج کریں۔",
        service: "سروس منتخب کریں۔",
        date: "تاریخ منتخب کریں۔",
        time: "وقت منتخب کریں۔",
        network: "بھیجنا ناکام۔ براہ کرم کال کریں",
      },
    },
    contact: {
      overline: "رابطہ کریں",
      heading1: "ٹائر چاہیے؟",
      heading2: "ابھی کال کریں۔",
      intro: "مدد حاصل کرنے کا تیز ترین طریقہ کال یا واٹس ایپ ہے — ہم عام طور پر سیکنڈوں میں جواب دیتے ہیں۔ یا انکوائری بھیجیں، ہم فوراً کال کریں گے۔",
      phoneLabel: "فون · 24/7",
      whatsappLabel: "واٹس ایپ",
      whatsappValue: "فٹر سے چیٹ کریں",
      emailLabel: "ای میل",
      serviceAreaLabel: "سروس ایریا",
      serviceAreaValue: "لیورپول اور گرد و نواح",
      formTitle: "انکوائری فارم",
      fields: {
        name: "آپ کا نام *",
        phone: "فون نمبر *",
        location: "آپ کا مقام *",
        tyreSize: "ٹائر سائز (اگر معلوم ہو)",
        message: "پیغام",
      },
      placeholders: {
        name: "جان اسمتھ",
        phone: "07...",
        location: "پوسٹ کوڈ یا علاقہ",
        tyreSize: "مثلاً 205/55 R16",
        message: "بتائیں کیا ہوا — پنکچر، بلوآؤٹ، گاڑی کا میک/ماڈل...",
      },
      disclaimer: "دن میں ہم منٹوں میں جواب دیتے ہیں، رات میں تیزی سے کال بیک کرتے ہیں۔",
      submit: "انکوائری بھیجیں",
      sending: "بھیجا جا رہا ہے...",
      success: "انکوائری بھیج دی گئی — ہم جلد کال کریں گے۔",
      errors: {
        missing: "نام، فون اور مقام درج کریں۔",
        network: "بھیجنا ناکام — براہ کرم کال کریں",
      },
    },
    footer: {
      overline: "ہنگامی ٹائر سروس",
      big1: "ٹائر پنکچر؟",
      big2: "کال",
      big3: "— ہم آپ کے پاس آئیں گے۔",
      callBtn: "ابھی کال کریں",
      whatsappBtn: "واٹس ایپ",
      desc: "لیورپول اور گرد و نواح کی 24/7 موبائل ٹائر فٹنگ سروس۔ وین دن رات سڑک پر۔",
      exploreLabel: "دیکھیں",
      contactLabel: "رابطہ",
      rights: "جملہ حقوق محفوظ ہیں۔",
      privacy: "پرائیویسی",
      terms: "شرائط",
      base: "24/7 · لیورپول اور گرد و نواح",
    },
    controls: { language: "زبان", theme: "تھیم", light: "روشن", dark: "گہرا" },
    policy: {
      label: "48 گھنٹے کی پالیسی",
      text: "کوئی بھی ٹائر کی تبدیلی، پنکچر کی مرمت، یا ٹائر سے متعلقہ کام صرف 48 گھنٹے کے لیے کور کیا جاتا ہے۔ اس 48 گھنٹے کی مدت کے بعد پیدا ہونے والے کسی بھی مسئلے کے ہم ذمہ دار نہیں ہیں۔",
      short: "صرف 48 گھنٹے کی کوریج — FAQ دیکھیں۔",
    },
    reviews: {
      overline: "گوگل جائزے",
      heading1: "5.0 ستارے۔",
      heading2: "اصلی صارفین سے۔",
      verified: "تصدیق شدہ",
      googleReviews: "گوگل جائزے",
      localGuide: "لوکل گائیڈ",
      review: "جائزہ",
      reviews: "جائزے",
      owner: "مالک",
      showReply: "مالک کا جواب",
      hideReply: "جواب چھپائیں",
      footnote: "ہر جائزہ ہمارے گوگل بزنس پروفائل سے حقیقی، تصدیق شدہ صارف ہے — گوگل سے براہ راست حاصل کردہ۔",
      writeBtn: "گوگل پر جائزہ چھوڑیں",
    },
    driverStatus: {
      online: "ڈرائیور آن لائن",
      updatedNow: "ابھی اپڈیٹ ہوا",
      heading1: "وین دستیاب ہے",
      heading2: "آپ کے قریب ابھی۔",
      sub: "ہمارا قریبی ڈرائیور لیورپول اور گرد و نواح میں تیار ہے — ابھی کال کریں، ہم فوری روانہ کریں گے۔",
      etaLabel: "اوسط آمد",
      etaValue: "30–40 منٹ",
      coverageLabel: "کوریج",
      coverageValue: "پورا مرسی سائیڈ",
      callBtn: "کال",
      liveMap: "براہ راست · لیورپول",
      mapAreaLabel: "لیورپول اور گرد و نواح",
      mapCaption: "وین سڑک پر",
    },
    howItWorks: {
      overline: "کیسے کام کرتا ہے",
      heading1: "آسان۔",
      heading2: "تین اقدامات۔",
      startNow: "ابھی شروع کریں",
      footnote: "بس یہی ہے — کوئی انتظار نہیں، کوئی قطار نہیں، کوئی ٹو نہیں۔ ہم آتے ہیں، فٹ کرتے ہیں، آپ چلے جاتے ہیں۔",
      steps: [
        { title: "کال یا واٹس ایپ کریں", desc: "07301 799929 پر کال کریں یا واٹس ایپ۔ ہمیں بتائیں آپ کہاں ہیں اور کیا ہوا — قیمت پہلے سے بتا دیں گے۔" },
        { title: "ڈرائیور راستے میں", desc: "ہمارا قریبی فٹر منٹوں میں آپ کی طرف روانہ ہے۔ ETA کال پر تصدیق ہوتی ہے۔" },
        { title: "فوری فٹنگ", desc: "سڑک، ڈرائیو وے یا پارکنگ — پوری ورکشاپ وین میں ہے۔ فوری فٹ کرتے ہیں، آپ روانہ ہو جاتے ہیں۔" },
      ],
    },
  },
  ar: {
    nav: { services: "الخدمات", areas: "المناطق", faq: "الأسئلة", book: "احجز", contact: "اتصل", call: "اتصل", whatsapp: "واتساب" },
    hero: {
      badge: "خدمة طوارئ 24/7 · ليفربول والمناطق المجاورة",
      line1: "إطار مثقوب؟",
      line2: "خدمة 24/7.",
      line3: "نأتي إليك.",
      sub: "خدمة تركيب الإطارات المتنقلة الموثوقة 24/7 في ليفربول والمناطق المجاورة. على الطريق، ممر السيارة أو مكان العمل —",
      subBold: "متوسط الوصول 30–40 دقيقة",
      callBtn: "اتصل",
      whatsappBtn: "راسلنا على واتساب",
      bookBtn: "احجز الآن",
      trust: {
        avgArrivalLabel: "متوسط الوصول", avgArrivalValue: "30–40 دقيقة",
        openNowLabel: "مفتوح الآن", openNowValue: "24/7 · 365",
        coverageLabel: "التغطية", coverageValue: "كل ميرسيسايد",
        insuredLabel: "مؤمن بالكامل", insuredValue: "فنيون محترفون",
      },
    },
    promise: {
      overline: "لماذا TyresPro",
      heading1: "سريع، محلي،",
      heading2: "ودائمًا نجيب.",
      cta: "اضغط للاتصال ·",
      items: [
        { headline: "30–40 دقيقة", sub: "متوسط الوصول" },
        { headline: "24 / 7", sub: "مفتوح ليل نهار" },
        { headline: "سيارات · شاحنات · 4x4", sub: "أي مركبة" },
        { headline: "ليفربول والمناطق المجاورة", sub: "نأتي إليك" },
        { headline: "مؤمن بالكامل", sub: "فنيون محترفون" },
      ],
    },
    services: {
      overline: "خدماتنا",
      heading1: "كل ما تحتاجه.",
      heading2: "أينما كنت.",
      sub: "من مكالمات الطوارئ إلى التبديلات المخطط لها — شاحناتنا المجهزة بالكامل تحضر الورشة إلى بابك أو ممر سيارتك أو الطريق.",
      badge247: "طوارئ · 24/7",
      cta: "اطلب شاحنة الآن ←",
      available: "متاح 24/7",
      cards: {
        emergency: { title: "خدمة تركيب الإطارات المتنقلة 24/7", desc: "معطل على الطريق؟ نتحرك فورًا في ليفربول والمناطق المجاورة — ليلًا أو نهارًا." },
        puncture:  { title: "إصلاح الثقب", desc: "إصلاح احترافي وسريع للثقوب في المكان — عودة سريعة إلى الطريق." },
        newtyres:  { title: "إطارات جديدة", desc: "مجموعة واسعة من الإطارات الجديدة تركب باحترافية في المكان للسيارات والشاحنات و 4x4." },
        parttyres: { title: "إطارات مستعملة", desc: "إطارات مستعملة عالية الجودة ومفحوصة أمانيًا — مركبة في المكان." },
        runflat:   { title: "إطارات Run-Flat", desc: "خدمة استبدال Run-Flat متخصصة لسيارات BMW و Mini والسيارات الرياضية." },
        vehicles:  { title: "سيارات · شاحنات · 4x4", desc: "جميع أنواع المركبات — من سيارات المدينة إلى الشاحنات التجارية." },
      },
    },
    areas: {
      overline: "المناطق المغطاة",
      heading1: "استجابة سريعة عبر",
      heading2: "ميرسيسايد.",
      sub: "أينما علقت — من وسط المدينة إلى الضواحي — سنكون معك في دقائق وليس ساعات.",
      note: "لست متأكدًا إن كنا نغطي منطقتك؟ اتصل بنا — نسافر أبعد للمهام العاجلة في شمال غرب إنجلترا.",
      chipsTitle: "لماذا يتصل الناس",
    },
    faq: {
      overline: "الأسئلة الشائعة",
      heading1: "إجابات سريعة.",
      heading2: "مساعدة أسرع.",
      sub: "لا يزال لديك سؤال؟ أسرع طريقة للإجابة هي الاتصال بنا مباشرة.",
      callBtn: "اتصل",
      items: [
        { q: "ما هي سياسة الـ 48 ساعة الخاصة بكم؟", a: "أي استبدال إطار، إصلاح ثقب، أو عمل متعلق بالإطارات ينفذ من قبل TyresPro Ltd مغطى لمدة 48 ساعة من وقت التركيب. نحن لسنا مسؤولين عن أي مشاكل تنشأ بعد فترة الـ 48 ساعة هذه. يرجى فحص الإطار قبل الانطلاق وإبلاغنا فورًا بأي مشكلة." },
      { q: "هل تعملون فعلًا 24 ساعة، 7 أيام في الأسبوع؟", a: "نعم. TyresPro Ltd خدمة إطارات متنقلة حقيقية 24/7. نرد على الهاتف ليلًا ونهارًا، عطلات نهاية الأسبوع والأعياد." },
        { q: "كم تستغرقون للوصول إلي؟", a: "معظم المهام في ليفربول والمناطق المجاورة نصل إليها خلال 30–40 دقيقة. في الطوارئ سنعطيك وقت وصول مؤكد عند الاتصال." },
        { q: "ما هي أنواع المركبات التي تركبون إطاراتها؟", a: "سيارات، شاحنات، مركبات تجارية خفيفة، 4x4 و SUV. لدينا إطارات ومعدات لعلامات تجارية عديدة." },
        { q: "هل يمكن إصلاح الثقب أم أحتاج إطارًا جديدًا؟", a: "إذا كان الضرر في منطقة قابلة للإصلاح، سنصلحه في المكان. إذا كان الإطار غير آمن، سنركب بديلًا — جديد أو مستعمل بجودة عالية." },
        { q: "هل تفرضون رسوم انتقال؟", a: "الأسعار شفافة ومؤكدة قبل السفر. توجد رسوم رمزية للانتقال في الطريق — نوضح كل شيء عبر الهاتف." },
        { q: "كيف يمكنني الدفع؟", a: "نقبل البطاقات، الدفع اللاصق، التحويل البنكي والنقد. نقدم إيصالًا لكل مهمة." },
      ],
    },
    booking: {
      overline: "احجز زيارة",
      heading1: "اختر يومًا.",
      heading2: "سنأتي إليك.",
      intro: "للمهام غير العاجلة — اختر تاريخًا ووقتًا يناسبك، وسنؤكد بالهاتف. للمساعدة العاجلة الآن، اتصل مباشرة.",
      bullets: [
        "مواعيد نفس اليوم متوفرة عادة",
        "منزل، عمل أو ممر سيارة — حيث يناسبك",
        "نؤكد بالهاتف قبل السفر",
      ],
      formTitle: "طلب حجز",
      fields: {
        name: "اسمك *",
        phone: "رقم الهاتف *",
        location: "موقعك *",
        vehicle: "المركبة (ماركة / موديل)",
        tyreSize: "مقاس الإطار (إن أمكن)",
        service: "الخدمة *",
        preferredDate: "التاريخ المفضل *",
        preferredTime: "الوقت المفضل *",
        notes: "ملاحظات",
      },
      placeholders: {
        name: "جون سميث",
        phone: "07...",
        location: "الرمز البريدي أو المنطقة",
        vehicle: "مثال: Ford Transit",
        tyreSize: "مثال: 205/55 R16",
        service: "اختر خدمة",
        date: "اختر تاريخًا",
        time: "اختر نافذة زمنية",
        notes: "أي شيء آخر يجب معرفته؟",
      },
      services: ["إطارات جديدة", "إطارات مستعملة", "إصلاح ثقب", "استبدال Run-Flat", "أخرى"],
      timeSlots: ["صباحًا (8ص – 12م)", "بعد الظهر (12م – 5م)", "مساءً (5م – 9م)", "أي وقت — اتصل للتأكيد"],
      disclaimer: "بإرسال هذا توافق على التواصل معك بالهاتف أو واتساب للتأكيد.",
      submit: "طلب الحجز",
      sending: "جارٍ الإرسال...",
      success: "تم استلام الحجز — سنعاود الاتصال قريبًا للتأكيد.",
      errors: {
        missing: "يرجى إدخال الاسم والهاتف والموقع.",
        service: "يرجى اختيار خدمة.",
        date: "يرجى اختيار تاريخ.",
        time: "يرجى اختيار وقت.",
        network: "فشل الإرسال. اتصل بنا على",
      },
    },
    contact: {
      overline: "تواصل معنا",
      heading1: "تحتاج إطارًا؟",
      heading2: "اتصل الآن.",
      intro: "أسرع طريقة للمساعدة هي الاتصال أو واتساب مباشرة — عادة نرد في ثوانٍ. أو أرسل استفسارًا وسنعاود الاتصال فورًا.",
      phoneLabel: "الهاتف · 24/7",
      whatsappLabel: "واتساب",
      whatsappValue: "دردش مع فني",
      emailLabel: "البريد الإلكتروني",
      serviceAreaLabel: "منطقة الخدمة",
      serviceAreaValue: "ليفربول والمناطق المجاورة",
      formTitle: "نموذج استفسار",
      fields: {
        name: "اسمك *",
        phone: "رقم الهاتف *",
        location: "موقعك *",
        tyreSize: "مقاس الإطار (إن أمكن)",
        message: "الرسالة",
      },
      placeholders: {
        name: "جون سميث",
        phone: "07...",
        location: "الرمز البريدي أو المنطقة",
        tyreSize: "مثال: 205/55 R16",
        message: "أخبرنا ما حدث — ثقب، انفجار، ماركة/موديل السيارة...",
      },
      disclaimer: "نستجيب في دقائق نهارًا، ونعاود الاتصال بسرعة ليلًا.",
      submit: "إرسال الاستفسار",
      sending: "جارٍ الإرسال...",
      success: "تم إرسال الاستفسار — سنعاود الاتصال قريبًا.",
      errors: {
        missing: "يرجى إدخال الاسم والهاتف والموقع.",
        network: "فشل الإرسال — يرجى الاتصال بنا مباشرة على",
      },
    },
    footer: {
      overline: "خدمة إطارات الطوارئ",
      big1: "إطار مثقوب؟",
      big2: "اتصل",
      big3: "— سنأتي إليك.",
      callBtn: "اتصل الآن",
      whatsappBtn: "واتساب",
      desc: "خدمة تركيب الإطارات المتنقلة 24/7 الأولى في ليفربول والمناطق المجاورة. شاحنات على الطريق ليل نهار.",
      exploreLabel: "استكشف",
      contactLabel: "تواصل",
      rights: "جميع الحقوق محفوظة.",
      privacy: "الخصوصية",
      terms: "الشروط",
      base: "24/7 · ليفربول والمناطق المجاورة",
    },
    controls: { language: "اللغة", theme: "الوضع", light: "فاتح", dark: "داكن" },
    policy: {
      label: "سياسة 48 ساعة",
      text: "أي استبدال إطار، إصلاح ثقب، أو عمل متعلق بالإطارات مغطى لمدة 48 ساعة فقط. نحن لسنا مسؤولين عن أي مشاكل تنشأ بعد فترة الـ 48 ساعة هذه.",
      short: "تغطية 48 ساعة فقط — راجع الأسئلة الشائعة.",
    },
    reviews: {
      overline: "تقييمات جوجل",
      heading1: "5.0 نجوم.",
      heading2: "من عملاء حقيقيين.",
      verified: "موثق",
      googleReviews: "تقييمات جوجل",
      localGuide: "مرشد محلي",
      review: "تقييم",
      reviews: "تقييمات",
      owner: "المالك",
      showReply: "رد المالك",
      hideReply: "إخفاء الرد",
      footnote: "كل تقييم من عميل حقيقي موثق على ملفنا في جوجل — يُجلب مباشرة من جوجل ولا يُعدَّل.",
      writeBtn: "اكتب تقييمًا على جوجل",
    },
    driverStatus: {
      online: "السائق متاح",
      updatedNow: "تم التحديث الآن",
      heading1: "شاحنة متاحة",
      heading2: "بالقرب منك الآن.",
      sub: "أقرب سائق لدينا في وضع الاستعداد في ليفربول والمناطق المجاورة — اتصل الآن وسنتحرك فورًا.",
      etaLabel: "متوسط الوصول",
      etaValue: "30–40 دقيقة",
      coverageLabel: "التغطية",
      coverageValue: "كل ميرسيسايد",
      callBtn: "اتصل",
      liveMap: "مباشر · ليفربول",
      mapAreaLabel: "ليفربول والمناطق المجاورة",
      mapCaption: "شاحنات على الطريق",
    },
    howItWorks: {
      overline: "كيف يعمل",
      heading1: "بسيط.",
      heading2: "ثلاث خطوات.",
      startNow: "ابدأ الآن",
      footnote: "هذا كل شيء — لا غرف انتظار، لا طوابير، لا سحب. نصل، نركب، تنطلق.",
      steps: [
        { title: "اتصل أو راسلنا على واتساب", desc: "اتصل بنا على 07301 799929 أو راسل واتساب. أخبرنا أين أنت وماذا حدث — نعطيك السعر مسبقًا." },
        { title: "السائق في الطريق", desc: "أقرب فني متاح متجه إليك خلال دقائق. نؤكد وقت الوصول عبر الهاتف قبل التحرك." },
        { title: "تركيب فوري", desc: "على الطريق أو الممر أو المرآب — ورشة كاملة على متن الشاحنة. نركب بسرعة، وأنت تنطلق." },
      ],
    },
  },
  zh: {
    nav: { services: "服务", areas: "服务区域", faq: "常见问题", book: "预约", contact: "联系", call: "来电", whatsapp: "WhatsApp" },
    hero: {
      badge: "24/7 紧急救援 · 利物浦及周边地区",
      line1: "轮胎爆胎？",
      line2: "24/7 服务。",
      line3: "我们上门。",
      sub: "利物浦及周边地区值得信赖的 24/7 移动轮胎安装服务。路边、车道或工作地点 —",
      subBold: "平均到达时间 30–40 分钟",
      callBtn: "致电",
      whatsappBtn: "WhatsApp 联系",
      bookBtn: "立即预约",
      trust: {
        avgArrivalLabel: "平均到达", avgArrivalValue: "30–40 分钟",
        openNowLabel: "现在营业", openNowValue: "24/7 · 全年",
        coverageLabel: "覆盖", coverageValue: "全默西塞德郡",
        insuredLabel: "全额保险", insuredValue: "专业技师",
      },
    },
    promise: {
      overline: "为什么选择 TyresPro",
      heading1: "快速、本地、",
      heading2: "随时接听。",
      cta: "点击拨打 ·",
      items: [
        { headline: "30–40 分钟", sub: "平均到达时间" },
        { headline: "24 / 7", sub: "全天营业" },
        { headline: "轿车 · 货车 · 4x4", sub: "任何车辆" },
        { headline: "利物浦及周边地区", sub: "我们上门" },
        { headline: "全额保险", sub: "专业技师" },
      ],
    },
    services: {
      overline: "我们的服务",
      heading1: "您所需的一切。",
      heading2: "就在您所在的地方。",
      sub: "从紧急呼叫到预约更换 — 我们配备齐全的货车将车间带到您家门口、车道或路边。",
      badge247: "紧急 · 24/7",
      cta: "现在派车 →",
      available: "24/7 可用",
      cards: {
        emergency: { title: "24/7 紧急移动轮胎安装", desc: "路上抛锚？我们在利物浦及周边地区立即出车 — 全天候。" },
        puncture:  { title: "补胎服务", desc: "现场专业快速补胎 — 立即恢复行驶。" },
        newtyres:  { title: "全新轮胎", desc: "为轿车、货车和 4x4 提供各种全新轮胎，专业现场安装。" },
        parttyres: { title: "半旧轮胎", desc: "经过安全检查的高品质半旧轮胎 — 全面检查并现场安装。" },
        runflat:   { title: "缺气保用轮胎", desc: "为宝马、Mini 和高性能车辆提供专业的 Run-Flat 更换服务。" },
        vehicles:  { title: "轿车 · 货车 · 4x4", desc: "覆盖所有车型 — 从城市小车到商用货车和 SUV。" },
      },
    },
    areas: {
      overline: "服务区域",
      heading1: "快速响应覆盖",
      heading2: "默西塞德郡。",
      sub: "无论您被困在哪里 — 从市中心到郊区车道 — 我们几分钟内就能到达，不用等几个小时。",
      note: "不确定我们是否覆盖您的地区？打个电话 — 我们为紧急工作会前往更远的西北地区。",
      chipsTitle: "客户为何联系我们",
    },
    faq: {
      overline: "常见问题",
      heading1: "快速解答。",
      heading2: "更快帮助。",
      sub: "还有问题？最快的方式是直接给我们打电话。",
      callBtn: "致电",
      items: [
        { q: "你们的 48 小时保修政策是什么？", a: "由 TyresPro Ltd 完成的任何轮胎更换、补胎或轮胎相关工作，自安装时起在 48 小时内享有保修。我们不对 48 小时期限之后出现的任何问题负责。请在离开前检查轮胎，如有异常请立即告知我们。" },
      { q: "你们真的 24 小时全天候营业吗？", a: "是的。TyresPro Ltd 是真正的 24/7 移动轮胎服务。我们全天候接听电话，包括周末和公共假期。" },
        { q: "你们多久能到达？", a: "利物浦及周边地区的大部分工作我们能在 30–40 分钟内到达。紧急情况下我们会在电话中确认到达时间。" },
        { q: "你们为哪些车辆安装轮胎？", a: "轿车、货车、轻型商用车、4x4 和 SUV。我们有各种品牌的轮胎和设备，包括缺气保用和低断面性能轮胎。" },
        { q: "补胎还是需要新胎？", a: "如果损伤在可修复区域且轮胎安全，我们会现场修补。如果轮胎不安全，我们会更换 — 全新或半旧，并预先报价。" },
        { q: "有出车费吗？", a: "价格透明，出发前确认。路边工作有少量出车费 — 电话中会说明清楚。" },
        { q: "如何付款？", a: "接受信用卡、非接触式支付、银行转账和现金。每项工作都提供收据。" },
      ],
    },
    booking: {
      overline: "预约上门",
      heading1: "选择日期。",
      heading2: "我们上门。",
      intro: "非紧急工作 — 选择适合您的日期和时间段，我们通过电话确认。需要紧急帮助请直接致电。",
      bullets: [
        "通常有当天可用时段",
        "家里、公司或车道 — 任何地方",
        "出发前电话确认",
      ],
      formTitle: "预约请求",
      fields: {
        name: "姓名 *",
        phone: "电话号码 *",
        location: "位置 *",
        vehicle: "车辆（品牌 / 型号）",
        tyreSize: "轮胎尺寸（如知道）",
        service: "服务 *",
        preferredDate: "首选日期 *",
        preferredTime: "首选时间 *",
        notes: "备注",
      },
      placeholders: {
        name: "张三",
        phone: "07...",
        location: "邮编或地区",
        vehicle: "例如 Ford Transit",
        tyreSize: "例如 205/55 R16",
        service: "选择服务",
        date: "选择日期",
        time: "选择时段",
        notes: "还有什么需要告诉我们的？",
      },
      services: ["全新轮胎", "半旧轮胎", "补胎", "缺气保用更换", "其他"],
      timeSlots: ["上午 (8am – 12pm)", "下午 (12pm – 5pm)", "晚上 (5pm – 9pm)", "任何时间 — 电话确认"],
      disclaimer: "提交即表示同意通过电话或 WhatsApp 联系确认。",
      submit: "提交预约",
      sending: "发送中...",
      success: "预约已收到 — 我们稍后会致电确认。",
      errors: {
        missing: "请填写姓名、电话和位置。",
        service: "请选择服务。",
        date: "请选择日期。",
        time: "请选择时间。",
        network: "发送失败。请致电",
      },
    },
    contact: {
      overline: "联系我们",
      heading1: "需要轮胎？",
      heading2: "立即致电。",
      intro: "获得帮助最快的方式是直接致电或 WhatsApp — 我们通常几秒内响应。或发送咨询，我们会立即回电。",
      phoneLabel: "电话 · 24/7",
      whatsappLabel: "WhatsApp",
      whatsappValue: "与技师聊天",
      emailLabel: "电子邮件",
      serviceAreaLabel: "服务区域",
      serviceAreaValue: "利物浦及周边地区",
      formTitle: "咨询表格",
      fields: {
        name: "姓名 *",
        phone: "电话号码 *",
        location: "位置 *",
        tyreSize: "轮胎尺寸（如知道）",
        message: "留言",
      },
      placeholders: {
        name: "张三",
        phone: "07...",
        location: "邮编或地区",
        tyreSize: "例如 205/55 R16",
        message: "告诉我们发生了什么 — 爆胎、故障、车辆品牌/型号...",
      },
      disclaimer: "白天我们几分钟内响应，夜间快速回电。",
      submit: "发送咨询",
      sending: "发送中...",
      success: "咨询已发送 — 我们稍后会致电您。",
      errors: {
        missing: "请填写姓名、电话和位置。",
        network: "发送失败 — 请直接致电",
      },
    },
    footer: {
      overline: "紧急轮胎服务",
      big1: "轮胎爆胎？",
      big2: "致电",
      big3: "— 我们上门。",
      callBtn: "立即致电",
      whatsappBtn: "WhatsApp",
      desc: "利物浦及周边地区首选的 24/7 移动轮胎安装服务。货车全天候在路上。",
      exploreLabel: "浏览",
      contactLabel: "联系",
      rights: "版权所有。",
      privacy: "隐私",
      terms: "条款",
      base: "24/7 · 利物浦及周边地区",
    },
    controls: { language: "语言", theme: "主题", light: "浅色", dark: "深色" },
    policy: {
      label: "48 小时保修政策",
      text: "任何轮胎更换、补胎或轮胎相关工作仅在 48 小时内保修。我们不对 48 小时期限之后出现的任何问题负责。",
      short: "仅提供 48 小时保修 — 详见常见问题。",
    },
    reviews: {
      overline: "谷歌评价",
      heading1: "5.0 星。",
      heading2: "来自真实客户。",
      verified: "已验证",
      googleReviews: "谷歌评价",
      localGuide: "当地向导",
      review: "评价",
      reviews: "评价",
      owner: "店主",
      showReply: "店主回复",
      hideReply: "隐藏回复",
      footnote: "每条评价均为真实经过谷歌验证的客户 — 从我们的谷歌商家资料自动获取。",
      writeBtn: "在谷歌留下评价",
    },
    driverStatus: {
      online: "司机在线",
      updatedNow: "刚刚更新",
      heading1: "货车就在",
      heading2: "您附近待命。",
      sub: "我们最近的司机正在利物浦及周边地区待命 — 立即致电，我们马上出发。",
      etaLabel: "预计到达",
      etaValue: "30–40 分钟",
      coverageLabel: "覆盖",
      coverageValue: "全默西塞德郡",
      callBtn: "致电",
      liveMap: "实时 · 利物浦覆盖",
      mapAreaLabel: "利物浦及周边地区",
      mapCaption: "货车正在路上",
    },
    howItWorks: {
      overline: "服务流程",
      heading1: "简单。",
      heading2: "三步搞定。",
      startNow: "立即开始",
      footnote: "就是这样 — 无需等候室、无需排队、无需拖车。我们上门、更换、您开走。",
      steps: [
        { title: "致电或 WhatsApp", desc: "拨打 07301 799929 或发 WhatsApp。告诉我们您在哪里发生了什么 — 先报价。" },
        { title: "司机正在路上", desc: "最近的可用技师几分钟内出发前往您所在位置。出发前电话确认到达时间。" },
        { title: "尽快装配", desc: "路边、车道或停车场 — 车载完整工作间。我们快速装配，您立即开走。" },
      ],
    },
  },
  ro: {
    nav: { services: "Servicii", areas: "Zone", faq: "Întrebări", book: "Programare", contact: "Contact", call: "Sună", whatsapp: "WhatsApp" },
    hero: {
      badge: "Urgență 24/7 · Liverpool și împrejurimi",
      line1: "Pană de cauciuc?",
      line2: "Serviciu 24/7.",
      line3: "Venim la tine.",
      sub: "Serviciu mobil de anvelope 24/7 de încredere în Liverpool și împrejurimi. La drum, la ușă sau la muncă —",
      subBold: "sosire medie 30–40 minute",
      callBtn: "Sună",
      whatsappBtn: "WhatsApp Acum",
      bookBtn: "Rezervă Acum",
      trust: {
        avgArrivalLabel: "Sosire medie", avgArrivalValue: "30–40 min",
        openNowLabel: "Deschis acum", openNowValue: "24/7 · 365",
        coverageLabel: "Acoperire", coverageValue: "Tot Merseyside",
        insuredLabel: "Asigurat complet", insuredValue: "Montatori pro",
      },
    },
    promise: {
      overline: "De ce TyresPro",
      heading1: "Rapid, local,",
      heading2: "și mereu răspundem.",
      cta: "Apasă pentru apel ·",
      items: [
        { headline: "30–40 min", sub: "sosire medie" },
        { headline: "24 / 7", sub: "deschis zi și noapte" },
        { headline: "Mașini · Dube · 4x4", sub: "orice vehicul" },
        { headline: "Liverpool și împrejurimi", sub: "venim la tine" },
        { headline: "Asigurat complet", sub: "montatori profesioniști" },
      ],
    },
    services: {
      overline: "Serviciile Noastre",
      heading1: "Tot ce ai nevoie.",
      heading2: "Chiar acolo unde ești.",
      sub: "De la urgențe la înlocuiri programate — dubele noastre complet echipate aduc atelierul la ușa ta, în alee sau pe drum.",
      badge247: "Urgență · 24/7",
      cta: "Cheamă o dubă acum →",
      available: "Disponibil 24/7",
      cards: {
        emergency: { title: "Montaj mobil anvelope 24/7", desc: "Ești blocat pe drum? Ne deplasăm imediat în Liverpool și împrejurimi — zi sau noapte." },
        puncture:  { title: "Reparație pană", desc: "Reparație rapidă și profesională pe loc — înapoi la drum imediat." },
        newtyres:  { title: "Anvelope Noi", desc: "Gamă largă de anvelope noi montate profesional pe loc pentru mașini, dube și 4x4." },
        parttyres: { title: "Anvelope Second-Hand", desc: "Anvelope second-hand de calitate, verificate — inspectate și montate pe loc." },
        runflat:   { title: "Anvelope Run-Flat", desc: "Serviciu specializat de înlocuire Run-Flat pentru BMW, Mini și mașini sport." },
        vehicles:  { title: "Mașini · Dube · 4x4", desc: "Toate tipurile de vehicule — de la mașini de oraș la dube comerciale și SUV-uri." },
      },
    },
    areas: {
      overline: "Zone Acoperite",
      heading1: "Răspuns rapid în",
      heading2: "Merseyside.",
      sub: "Oriunde ai fi blocat — din centrul orașului până în suburbie — ajungem în minute, nu ore.",
      note: "Nu ești sigur dacă acoperim zona ta? Sună — mergem mai departe pentru urgențe în nord-vestul Angliei.",
      chipsTitle: "De ce ne sună clienții",
    },
    faq: {
      overline: "Întrebări Frecvente",
      heading1: "Răspunsuri rapide.",
      heading2: "Ajutor mai rapid.",
      sub: "Mai ai o întrebare? Cel mai rapid mod de a primi răspuns este să ne suni.",
      callBtn: "Sună",
      items: [
        { q: "Care este politica voastră de garanție de 48 de ore?", a: "Orice înlocuire de anvelopă, reparație de pană sau lucrare legată de anvelope efectuată de TyresPro Ltd este acoperită timp de 48 de ore de la momentul montajului. Nu suntem responsabili pentru problemele apărute după această perioadă de 48 de ore. Vă rugăm să verificați anvelopa înainte de plecare și să ne anunțați imediat orice problemă." },
      { q: "Chiar operați 24 de ore, 7 zile pe săptămână?", a: "Da. TyresPro Ltd este un serviciu mobil autentic 24/7. Răspundem la telefon zi și noapte, inclusiv weekend și sărbători." },
        { q: "Cât de repede ajungeți la mine?", a: "Majoritatea lucrărilor în Liverpool și împrejurimi sunt rezolvate în 30–40 minute. Pentru urgențe îți vom da un ETA confirmat la apel." },
        { q: "Ce tipuri de vehicule deserviți?", a: "Mașini, dube, vehicule comerciale ușoare, 4x4 și SUV-uri. Avem stoc și echipament pentru multe mărci, inclusiv Run-Flat." },
        { q: "Se poate repara pana sau am nevoie de anvelopă nouă?", a: "Dacă deteriorarea este într-o zonă reparabilă, o reparăm pe loc. Dacă nu este sigură, montăm una nouă sau second-hand — cu preț confirmat înainte." },
        { q: "Percepeți taxă de deplasare?", a: "Prețurile sunt transparente și confirmate înainte. Există o mică taxă de deplasare pentru urgențe — totul se explică la telefon." },
        { q: "Cum plătesc?", a: "Acceptăm card, contactless, transfer bancar și cash. Emitem chitanță pentru fiecare lucrare." },
      ],
    },
    booking: {
      overline: "Rezervă o vizită",
      heading1: "Alege o zi.",
      heading2: "Venim la tine.",
      intro: "Pentru lucrări neurgente — alege o dată și un interval, confirmăm la telefon. Pentru urgență, sună direct oricând.",
      bullets: [
        "Sloturi în aceeași zi disponibile de obicei",
        "Acasă, la muncă sau în alee — cum îți convine",
        "Confirmăm la telefon înainte de deplasare",
      ],
      formTitle: "Cerere de Rezervare",
      fields: {
        name: "Numele tău *",
        phone: "Număr de telefon *",
        location: "Locația ta *",
        vehicle: "Vehicul (marcă / model)",
        tyreSize: "Dimensiune anvelopă (dacă știi)",
        service: "Serviciu *",
        preferredDate: "Data preferată *",
        preferredTime: "Ora preferată *",
        notes: "Note",
      },
      placeholders: {
        name: "Ion Popescu",
        phone: "07...",
        location: "Cod poștal sau zonă",
        vehicle: "ex. Ford Transit",
        tyreSize: "ex. 205/55 R16",
        service: "Alege un serviciu",
        date: "Alege o dată",
        time: "Alege un interval",
        notes: "Altceva ce trebuie să știm?",
      },
      services: ["Anvelope Noi", "Anvelope Second-Hand", "Reparație Pană", "Înlocuire Run-Flat", "Altele"],
      timeSlots: ["Dimineața (8am – 12pm)", "După-amiaza (12pm – 5pm)", "Seara (5pm – 9pm)", "Oricând — confirmăm la telefon"],
      disclaimer: "Prin trimitere ești de acord să fii contactat prin telefon sau WhatsApp.",
      submit: "Trimite Cererea",
      sending: "Se trimite...",
      success: "Rezervare primită — te sunăm curând pentru confirmare.",
      errors: {
        missing: "Adaugă numele, telefonul și locația.",
        service: "Alege un serviciu.",
        date: "Alege o dată.",
        time: "Alege o oră.",
        network: "Nu s-a putut trimite. Sună-ne la",
      },
    },
    contact: {
      overline: "Ia Legătura",
      heading1: "Ai nevoie de anvelopă?",
      heading2: "Sună-ne acum.",
      intro: "Cel mai rapid mod este apel sau WhatsApp — de obicei răspundem în secunde. Sau trimite o cerere și te sunăm noi.",
      phoneLabel: "Telefon · 24/7",
      whatsappLabel: "WhatsApp",
      whatsappValue: "Discută cu un montator",
      emailLabel: "Email",
      serviceAreaLabel: "Zona de Servire",
      serviceAreaValue: "Liverpool și împrejurimi",
      formTitle: "Formular Cerere",
      fields: {
        name: "Numele tău *",
        phone: "Număr de telefon *",
        location: "Locația ta *",
        tyreSize: "Dimensiune anvelopă (dacă știi)",
        message: "Mesaj",
      },
      placeholders: {
        name: "Ion Popescu",
        phone: "07...",
        location: "Cod poștal sau zonă",
        tyreSize: "ex. 205/55 R16",
        message: "Spune-ne ce s-a întâmplat — pană, explozie, marcă/model...",
      },
      disclaimer: "Ziua răspundem în minute, noaptea sunăm rapid înapoi.",
      submit: "Trimite Cererea",
      sending: "Se trimite...",
      success: "Cerere trimisă — te sunăm curând.",
      errors: {
        missing: "Completează numele, telefonul și locația.",
        network: "Nu s-a putut trimite — sună-ne direct la",
      },
    },
    footer: {
      overline: "Serviciu Urgență Anvelope",
      big1: "Pană de cauciuc?",
      big2: "Sună",
      big3: "— venim la tine.",
      callBtn: "Sună Acum",
      whatsappBtn: "WhatsApp",
      desc: "Serviciul mobil de anvelope numărul 1 din Liverpool și împrejurimi, 24/7. Dubele noastre sunt pe drum zi și noapte.",
      exploreLabel: "Explorează",
      contactLabel: "Contact",
      rights: "Toate drepturile rezervate.",
      privacy: "Confidențialitate",
      terms: "Termeni",
      base: "24/7 · Liverpool și împrejurimi",
    },
    controls: { language: "Limbă", theme: "Temă", light: "Deschis", dark: "Închis" },
    policy: {
      label: "Garanție 48 de ore",
      text: "Orice înlocuire de anvelopă, reparație de pană sau lucrare legată de anvelope este acoperită doar 48 de ore. Nu suntem responsabili pentru problemele apărute după această perioadă de 48 de ore.",
      short: "Garanție doar 48 de ore — vezi FAQ.",
    },
    reviews: {
      overline: "Recenzii Google",
      heading1: "5.0 stele.",
      heading2: "De la clienți reali.",
      verified: "Verificat",
      googleReviews: "recenzii Google",
      localGuide: "Ghid Local",
      review: "recenzie",
      reviews: "recenzii",
      owner: "Proprietar",
      showReply: "Răspunsul proprietarului",
      hideReply: "Ascunde răspunsul",
      footnote: "Fiecare recenzie este un client real, verificat pe Profilul nostru Google — preluat direct de la Google.",
      writeBtn: "Scrie o recenzie Google",
    },
    driverStatus: {
      online: "Șofer online",
      updatedNow: "Actualizat acum",
      heading1: "O dubă este disponibilă",
      heading2: "aproape de tine acum.",
      sub: "Cel mai apropiat șofer al nostru este în așteptare în Liverpool și împrejurimi — sună acum și pornim imediat.",
      etaLabel: "Sosire estimată",
      etaValue: "30–40 min",
      coverageLabel: "Acoperire",
      coverageValue: "Tot Merseyside",
      callBtn: "Sună",
      liveMap: "Live · Liverpool",
      mapAreaLabel: "Liverpool și împrejurimi",
      mapCaption: "Dube pe drum",
    },
    howItWorks: {
      overline: "Cum Funcționează",
      heading1: "Simplu.",
      heading2: "Trei pași.",
      startNow: "Începe acum",
      footnote: "Asta e — fără săli de așteptare, fără cozi, fără remorcare. Venim, montăm, pleci.",
      steps: [
        { title: "Sună sau scrie pe WhatsApp", desc: "Sună-ne la 07301 799929 sau scrie pe WhatsApp. Spune-ne unde ești și ce s-a întâmplat — cotăm în avans." },
        { title: "Șoferul e pe drum", desc: "Cel mai apropiat montator disponibil vine spre tine în câteva minute. ETA confirmat la telefon înainte de plecare." },
        { title: "Montare ASAP", desc: "Pe drum, în alee sau parcare — atelier complet la bord. Montăm rapid, pleci." },
      ],
    },
  },
};

export default translations;
